import { cineamoApiClient } from '../../apiClient';
import { UrlQueryParameter, buildUrlQuery } from '../../helpers/url-query-helper';
import {
    CinemaProductDto,
    CinemaProductInputDto,
    CinemaProductSummaryDto,
    CinemaProductSyncResult,
    CinemaProductUpdateDto
} from '../../models/cinema-product/CinemaProductDto.types';
import { ApiErrorResponse } from '../../types/ApiErrorResponse.types';
import {
    CinemaProductListResponse,
    CinemaProductSummaryListResponse,
    TicketSystemArticleListResponse
} from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';

// <------------------------ Cinema Products ------------------------>

type GetCinemaProductsParams = UrlQueryParameter & {
    cinemaId?: number | string;
    cinemaProductCategoryId?: number | string;
    isActive?: boolean;
    /** true: linked to an article of the ticket system; false: not linked. */
    ticketSystemLinked?: boolean;
    /** Only what the cinema website may sell: active, and linked when the cinema is on Cinetixx. */
    sellableOnline?: boolean;
    /**
     * Also ask the ticket system for its products. Honored only for operators of the product's cinema
     * and admins; everyone else gets `isTicketSystemChecked: false`.
     */
    withTicketSystemCheck?: boolean;
};

/**
 * This endpoint returns a list of cinema products.
 *
 * @example
 * // Get products offerd by a specific cinema:
 * getCinemaProductsByCinemaId({cinemaId: 1});
 *
 * @param urlParams
 * @returns {ApiResponse<CinemaProductListResponse>}
 */

export async function getCinemaProductsByCinemaId(
    urlParams?: GetCinemaProductsParams
): Promise<ApiResponse<CinemaProductListResponse> | null> {
    return cineamoApiClient
        .get<ApiResponse<CinemaProductListResponse>>(`/cinema-products${buildUrlQuery(urlParams)}`)
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

// <------------------------ Cinema Product ------------------------>

/**
 * This endpoint returns a cinema product.
 *
 * @example
 * // Get single product by id:
 * getCinemaProductById(1);
 *
 * @param id
 * @returns {ApiResponse<CinemaProductDto>}
 */

export async function getCinemaProductById(
    id: number | string,
    urlParams?: Pick<GetCinemaProductsParams, 'withTicketSystemCheck'>
): Promise<ApiResponse<CinemaProductDto> | null> {
    return cineamoApiClient
        .get<ApiResponse<CinemaProductDto>>(`/cinema-products/${id}${buildUrlQuery(urlParams)}`)
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

// <------------------------ Create Cinema Product ------------------------>

/**
 * This endpoint returns a cinema product.
 *
 * @example
 * // Create a single product to a cinema:
 * getCinemaProductById({...cinemaProduct});
 *
 * @param cinemaProduct
 * @returns {ApiResponse<CinemaProductDto> | ApiErrorResponse<CinemaProductDto>}
 */

export async function createCinemaProduct(
    cinemaProduct: CinemaProductInputDto
): Promise<ApiResponse<CinemaProductDto> | ApiErrorResponse<CinemaProductInputDto>> {
    return cineamoApiClient
        .post('/cinema-products', cinemaProduct)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Sync Cinema Products ------------------------>

/**
 * Syncs a cinema's products with its ticket system's product catalog (Cinetixx only): linked products get its
 * price, unlinked products with the same name are linked, missing articles become inactive products, and
 * linked products whose article is gone are switched off.
 *
 * @returns the summary, or the error (502: the ticket system could not be asked; 400: no product catalog)
 */
export async function syncCinemaProducts(
    cinemaId: number | string
): Promise<CinemaProductSyncResult | ApiErrorResponse<null>> {
    return cineamoApiClient
        .post<CinemaProductSyncResult>('/cinema-products/sync', { cinema: cinemaId })
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Cinema Product Summary ------------------------>

/** Counts of a cinema's products and its last sync. Operators of the cinema and admins only. */
export async function getCinemaProductSummary(cinemaId: number | string): Promise<CinemaProductSummaryDto | null> {
    return cineamoApiClient
        .get<CinemaProductSummaryDto>(`/cinema-products/summary${buildUrlQuery({ cinemaId })}`)
        .then((response) => response.data)
        .catch(() => null);
}

/**
 * Admin: the summary of every cinema with online concession sale on, cinemas needing attention first.
 * HAL list, `_embedded['cinema-product-summaries']`.
 */
export async function getCinemaProductSummaries(
    urlParams?: UrlQueryParameter
): Promise<ApiResponse<CinemaProductSummaryListResponse> | null> {
    return cineamoApiClient
        .get<ApiResponse<CinemaProductSummaryListResponse>>(`/cinema-product-summaries${buildUrlQuery(urlParams)}`)
        .then((response) => response.data)
        .catch(() => null);
}

// <------------------------ Ticket System Articles ------------------------>

/**
 * The cinema's ticket system articles that no product is linked to yet, for the link picker (Cinetixx only).
 * HAL list, `_embedded['ticket-system-articles']`.
 */
export async function getUnlinkedTicketSystemArticles(
    urlParams: UrlQueryParameter & { cinemaId: number | string }
): Promise<ApiResponse<TicketSystemArticleListResponse> | ApiErrorResponse<null>> {
    return cineamoApiClient
        .get<ApiResponse<TicketSystemArticleListResponse>>(
            `/cinema-products/ticket-system-articles${buildUrlQuery(urlParams)}`
        )
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Bulk Update Cinema Products ------------------------>

/** Switches several products of one cinema on or off at once. Returns how many were changed. */
export async function bulkUpdateCinemaProducts(body: {
    cinema: number | string;
    ids: CinemaProductDto['id'][];
    isActive: boolean;
}): Promise<{ updated: number } | ApiErrorResponse<null>> {
    return cineamoApiClient
        .patch<{ updated: number }>('/cinema-products', body)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Upload Cinema Product Image ------------------------>

/** Mirrors the server's cap; the deployed api rejects larger requests at the edge. */
export const CINEMA_PRODUCT_IMAGE_MAX_BYTES = 4 * 1024 * 1024;

/**
 * Uploads the product's image (png, jpeg, gif or webp, at most 4MB) and stores it on the product.
 *
 * @returns the updated product, or the error (422 carries the reason)
 */
export async function uploadCinemaProductImage(
    cinemaProductId: number | string,
    file: File
): Promise<ApiResponse<CinemaProductDto> | ApiErrorResponse<null>> {
    const formData = new FormData();
    formData.append('file', file);

    return cineamoApiClient
        .post<ApiResponse<CinemaProductDto>>(`/cinema-products/${cinemaProductId}/image`, formData, {
            timeout: 60000
        })
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Update Cinema Product ------------------------>

/**
 * This endpoint returns a cinema product.
 *
 * @example
 * // Update a single cinema product:
 * updateCinemaProductById(1, {...cinemaProduct});
 *
 * @param cinemaProduct
 * @param cinemaProductId
 * @returns {ApiResponse<CinemaProductDto> | ApiErrorResponse<CinemaProductDto>}
 */

export async function updateCinemaProductById(
    cinemaProductId: number | string,
    cinemaProduct: CinemaProductUpdateDto
): Promise<ApiResponse<CinemaProductDto> | ApiErrorResponse<CinemaProductUpdateDto>> {
    return cineamoApiClient
        .patch(`/cinema-products/${cinemaProductId}`, cinemaProduct)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Set Cinema Product Category ------------------------>

/**
 * This endpoint returns a cinema product.
 *
 * @example
 * // Update a single cinema product categories:
 * setCinemaProductCategory(1, 1);
 *
 * @param cinemaProductId
 * @param categoryId
 * @returns {ApiResponse<CinemaProductDto> | ApiErrorResponse<CinemaProductDto>}
 */

type SetCinemaProductCategoriesParams = UrlQueryParameter & {
    /** The full set; an empty list removes every category. */
    categories: (number | string)[];
};

export async function setCinemaProductCategories(
    cinemaProductId: number | string,
    categories: SetCinemaProductCategoriesParams
): Promise<ApiResponse<CinemaProductDto> | ApiErrorResponse<CinemaProductDto>> {
    return cineamoApiClient
        .put(`/cinema-products/${cinemaProductId}/categories`, categories)
        .then((response) => response.data)
        .catch((error) => error);
}

// <------------------------ Delete Cinema Product ------------------------>

/**
 * This endpoint returns a boolean.
 *
 * @example
 * // Delete a single cinema product by id:
 * deleteCinemaProductById(1);
 *
 * @param cinemaProduct
 * @returns {boolean}
 */

export async function deleteCinemaProductById(cinemaProduct: number | string): Promise<boolean> {
    return cineamoApiClient
        .delete(`/cinema-products/${cinemaProduct}`)
        .then((response) => response.status === 204)
        .catch(() => {
            return false;
        });
}
